import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import Div from "@jumbo/shared/Div";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import {
  Grid,
  Divider,
  Typography,
  Alert,
  AlertTitle,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

const roundPrice = (n) => (Number.isFinite(n) ? Number(n.toFixed(2)) : "");

/**
 * Renders the "Vernostný program" (Loyalty program) section with 3 levels.
 * Each level: isAvailable, discount type (amount | percent), discounted price or percent + computed price.
 * Only shown when loyaltyEnabled && loyaltyType !== "none".
 */
const LoyaltyProgramSection = ({ values, setFieldValue, t, currencyCode }) => {
  const { errors } = useFormikContext() || {};
  const loyaltyData = Array.isArray(values.loyaltyData)
    ? values.loyaltyData
    : [];
  const basePrice = Number(values.price);

  const getEntryForLevel = (level) =>
    loyaltyData.find((e) => e.level === level) || loyaltyData[level - 1];

  const getPriceForLevel = (level) => {
    const entry = getEntryForLevel(level);
    return entry?.discountedPrice !== undefined &&
      entry?.discountedPrice !== null
      ? entry.discountedPrice
      : "";
  };

  const getIsAvailableForLevel = (level) => {
    const entry = getEntryForLevel(level);
    return entry?.isAvailable === true;
  };

  const getDiscountTypeForLevel = (level) => {
    const entry = getEntryForLevel(level);
    return entry?.discountType === "percent" ? "percent" : "amount";
  };

  const getDiscountPercentForLevel = (level) => {
    const entry = getEntryForLevel(level);
    return entry?.discountPercent !== undefined &&
      entry?.discountPercent !== null
      ? entry.discountPercent
      : "";
  };

  /** Pri type percent vráti cenu vypočítanú z basePrice a percenta (okamžité zobrazenie), inak uloženú cenu. */
  const getDisplayPriceForLevel = (level) => {
    const entry = getEntryForLevel(level);
    if (entry?.discountType === "percent") {
      const pct = entry.discountPercent;
      const pctNum =
        pct !== "" && pct !== undefined && pct !== null ? Number(pct) : NaN;
      if (
        Number.isFinite(pctNum) &&
        pctNum >= 0 &&
        pctNum <= 100 &&
        Number.isFinite(basePrice) &&
        basePrice >= 0
      ) {
        return roundPrice(basePrice * (1 - pctNum / 100));
      }
      return "";
    }
    return getPriceForLevel(level);
  };

  // When base price changes, recompute discountedPrice for all percent-type levels (dependency only on basePrice to avoid loops)
  useEffect(() => {
    if (!Number.isFinite(basePrice) || basePrice < 0) return;
    const raw = values.loyaltyData || [];
    const arr = [1, 2, 3].map((level) => {
      const i = level - 1;
      return raw[i] || raw.find((e) => e?.level === level) || { level };
    });
    let changed = false;
    for (let level = 1; level <= 3; level++) {
      const index = level - 1;
      const entry = arr[index];
      if (entry?.discountType === "percent") {
        const pct = entry.discountPercent;
        const pctNum =
          pct !== "" && pct !== undefined && pct !== null ? Number(pct) : NaN;
        if (Number.isFinite(pctNum) && pctNum >= 0 && pctNum <= 100) {
          const computed = roundPrice(basePrice * (1 - pctNum / 100));
          if (entry.discountedPrice !== computed) {
            arr[index] = { ...entry, discountedPrice: computed };
            changed = true;
          }
        }
      }
    }
    if (changed) setFieldValue("loyaltyData", arr);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run when base price changes to recompute percent-based prices
  }, [basePrice]);

  const handleDiscountTypeChange = (index, newType) => {
    const raw = values.loyaltyData || [];
    const arr = [1, 2, 3].map((level) => {
      const i = level - 1;
      return raw[i] || raw.find((e) => e?.level === level) || { level };
    });
    const entry = arr[index] || { level: index + 1 };
    const updated = {
      ...entry,
      level: index + 1,
      discountType: newType,
      translationCode: entry.translationCode || `Level${index + 1}`,
      isAvailable: entry.isAvailable === true,
    };
    if (newType === "percent") {
      updated.discountPercent = "";
      updated.discountedPrice = "";
    } else {
      updated.discountPercent = "";
      updated.discountedPrice = entry.discountedPrice ?? "";
    }
    arr[index] = updated;
    setFieldValue("loyaltyData", arr);
  };

  const handlePercentChange = (index, rawValue) => {
    const val = rawValue === "" ? "" : Number(rawValue);
    const raw = values.loyaltyData || [];
    const arr = [1, 2, 3].map((level) => {
      const i = level - 1;
      return raw[i] || raw.find((e) => e?.level === level) || { level };
    });
    const entry = arr[index] || { level: index + 1 };
    const updatedEntry = {
      ...entry,
      level: index + 1,
      discountType: "percent",
      discountPercent: val,
      translationCode: entry.translationCode || `Level${index + 1}`,
      isAvailable: entry.isAvailable === true,
    };
    if (
      val !== "" &&
      Number.isFinite(Number(val)) &&
      Number(val) >= 0 &&
      Number(val) <= 100 &&
      Number.isFinite(basePrice) &&
      basePrice >= 0
    ) {
      updatedEntry.discountedPrice = roundPrice(
        basePrice * (1 - Number(val) / 100),
      );
    } else {
      updatedEntry.discountedPrice = "";
    }
    arr[index] = updatedEntry;
    setFieldValue("loyaltyData", arr);
  };

  /** Pri opustení poľa percenta vždy prepočíta cenu pre danú úroveň a uloží do formulára. */
  const handlePercentBlur = (index) => {
    const raw = values.loyaltyData || [];
    const arr = [1, 2, 3].map((level) => {
      const i = level - 1;
      return raw[i] || raw.find((e) => e?.level === level) || { level };
    });
    const entry = arr[index];
    if (!entry || entry.discountType !== "percent") return;
    const pct = entry.discountPercent;
    const pctNum =
      pct !== "" && pct !== undefined && pct !== null ? Number(pct) : NaN;
    const newPrice =
      Number.isFinite(pctNum) &&
      pctNum >= 0 &&
      pctNum <= 100 &&
      Number.isFinite(basePrice) &&
      basePrice >= 0
        ? roundPrice(basePrice * (1 - pctNum / 100))
        : "";
    arr[index] = { ...entry, discountedPrice: newPrice };
    setFieldValue("loyaltyData", arr);
  };

  return (
    <>
      <Grid item xs={12} md={12} lg={12} sx={{ mt: 3 }}>
        <Typography variant="h4" color="gray" my={1}>
          {t("pages.dsokProducts.loyaltyProgram.title", {
            defaultValue: "Vernostný program",
          })}
        </Typography>
      </Grid>

      {/* Info block – same style as Time Based Prices */}
      <Grid item xs={12} md={12} lg={12} sx={{ mb: 2 }}>
        <Alert severity="info" variant="outlined">
          <AlertTitle>
            {t("pages.dsokProducts.loyaltyProgram.info.title", {
              defaultValue: "Vernostný program – ako to funguje",
            })}
          </AlertTitle>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t("pages.dsokProducts.loyaltyProgram.info.description", {
              defaultValue:
                "Pre každú úroveň nastavte Aktívna, typ zľavy (Suma alebo Percento), prípadne percento a cenu. Pri type Suma zadáte cenu priamo; pri Percento zadáte percento a cena sa vypočíta zo základnej ceny produktu.",
            })}
          </Typography>
        </Alert>
      </Grid>

      <Grid container columnSpacing={2}>
        {[1, 2, 3].map((level) => {
          const index = level - 1;
          const discountType = getDiscountTypeForLevel(level);
          const isPercent = discountType === "percent";
          const percentError =
            (errors?.loyaltyData &&
              errors.loyaltyData[index]?.discountPercent) ||
            errors?.[`loyaltyData.${index}.discountPercent`];
          const percentHelpDefault = t(
            "pages.dsokProducts.loyaltyProgram.percentHelp",
            {
              defaultValue:
                "Povinné. Zadané percento sa použije na výpočet výslednej ceny pre túto úroveň.",
            },
          );
          return (
            <Grid item xs={12} key={`loyalty-level-${level}`}>
              <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ mt: 1, mb: 1 }}
              >
                <Grid item xs={12} sm="auto" sx={{ minWidth: 90 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t(`pages.dsokProducts.loyaltyProgram.level${level}`, {
                      defaultValue: `Úroveň ${level}`,
                    })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={getIsAvailableForLevel(level)}
                        onChange={(e) =>
                          setFieldValue(
                            `loyaltyData.${index}.isAvailable`,
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label={t("pages.dsokProducts.loyaltyProgram.isAvailable", {
                      defaultValue: "Aktívna",
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>
                      {t(
                        "pages.dsokProducts.loyaltyProgram.discountTypeLabel",
                        {
                          defaultValue: "Typ zľavy",
                        },
                      )}
                    </InputLabel>
                    <Select
                      label={t(
                        "pages.dsokProducts.loyaltyProgram.discountTypeLabel",
                        {
                          defaultValue: "Typ zľavy",
                        },
                      )}
                      value={discountType}
                      onChange={(e) =>
                        handleDiscountTypeChange(index, e.target.value)
                      }
                    >
                      <MenuItem value="amount">
                        {t(
                          "pages.dsokProducts.loyaltyProgram.discountTypeAmount",
                          {
                            defaultValue: "Suma",
                          },
                        )}
                      </MenuItem>
                      <MenuItem value="percent">
                        {t(
                          "pages.dsokProducts.loyaltyProgram.discountTypePercent",
                          {
                            defaultValue: "Percento z ceny",
                          },
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {isPercent && (
                  <Grid item xs={12} sm={3} md={2}>
                    <JumboTextField
                      type="number"
                      fullWidth
                      size="small"
                      name={`loyaltyData.${index}.discountPercent`}
                      value={getDiscountPercentForLevel(level)}
                      label={t(
                        "pages.dsokProducts.loyaltyProgram.percentLabel",
                        {
                          defaultValue: "Percento",
                        },
                      )}
                      InputLabelProps={{ required: true }}
                      InputProps={{
                        inputProps: { min: 0, max: 100, step: 0.01 },
                        endAdornment: <span style={{ marginLeft: 4 }}>%</span>,
                      }}
                      onChange={(e) =>
                        handlePercentChange(index, e.target.value)
                      }
                      onBlur={() => handlePercentBlur(index)}
                      error={!!percentError}
                      helperText={percentError || percentHelpDefault}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={3} md={2}>
                  {isPercent ? (
                    <TextField
                      type="number"
                      fullWidth
                      size="small"
                      value={getDisplayPriceForLevel(level)}
                      label={t("pages.dsokProducts.loyaltyProgram.priceLabel", {
                        defaultValue: "Cena",
                      })}
                      InputProps={{
                        readOnly: true,
                        inputProps: { min: 0 },
                        endAdornment: currencyCode ? (
                          <span style={{ marginLeft: 4 }}>{currencyCode}</span>
                        ) : null,
                      }}
                    />
                  ) : (
                    <JumboTextField
                      type="number"
                      fullWidth
                      size="small"
                      name={`loyaltyData.${index}.discountedPrice`}
                      value={getPriceForLevel(level)}
                      label={t("pages.dsokProducts.loyaltyProgram.priceLabel", {
                        defaultValue: "Cena",
                      })}
                      InputLabelProps={{ required: false }}
                      InputProps={{
                        inputProps: { min: 0, step: 0.01 },
                        endAdornment: currencyCode ? (
                          <span style={{ marginLeft: 4 }}>{currencyCode}</span>
                        ) : null,
                      }}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFieldValue(
                          `loyaltyData.${index}.discountedPrice`,
                          val === "" ? "" : Number(val),
                        );
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </>
  );
};

export default LoyaltyProgramSection;
